/**
 * Перевод отказов бэкенда в тексты для игрока, подающего заявку по ссылке.
 *
 * Две особенности контракта:
 *
 * 1. `ExceptionListener` (`imgame-backend/src/EventListener/ExceptionListener.php`)
 *    отдаёт осмысленный статус только наследникам `HttpException`. Всё остальное —
 *    ошибки JWT, формат даты, лимит заявок, тип файла — приходит как **500** с
 *    телом `{"code":500,"message":"Internal error: <текст исключения>"}`. Кода в
 *    смысле `BackendErrorCode` у этих отказов нет — их узнают правила «по тексту»
 *    общего реестра (`shared/errors`, контекст `APPLICATION_LINK`), а не этот файл.
 * 2. Превышение 5 МБ режет nginx (`client_max_body_size 5M`), и в ответ прилетает
 *    **HTML**, а не JSON. Разбирать `message` там нечего — нужен статус.
 *
 * Тексты переводятся в момент разбора ошибки: словарь уровня модуля из строк застыл
 * бы на языке старта.
 */
import { msg, t } from '@lingui/core/macro';
import { BackendErrorContext, BackendErrorText, getBackendErrorText, getErrorReason, GetErrorReasonOptions } from 'shared/errors';
import { i18n } from 'shared/i18n';

/** Максимальный размер тела запроса — `client_max_body_size` в nginx. */
export const MAX_REQUEST_SIZE_MB = 5;

export interface ApplicationErrorResult {
  message: string;
  /**
   * Заявка на бэкенде уже создана, несмотря на ошибку.
   *
   * Контроллер создаёт и коммитит заявку, и только потом грузит фото в S3
   * (`ChampionshipApplicationsController.php:193-204`). Падение загрузки даёт
   * клиенту 500 при существующей заявке — предлагать отправить её заново нельзя.
   */
  applicationCreated: boolean;
}

const GENERIC = msg`Не удалось отправить заявку. Попробуйте позже или обратитесь к организатору`;
const NETWORK = msg`Нет связи с сервером. Проверьте интернет и попробуйте ещё раз`;
const BROKEN_LINK = msg`Ссылка повреждена. Запросите новую у организатора`;

/**
 * Разбор — общий; здесь только текст, который на публичной странице точнее общего. Число лимита
 * сюда не подставляется: настроек турнира у страницы нет, а вытаскивать его регуляркой из
 * английского текста ошибки план IMGM-1549 запретил.
 */
const LINK_REASON_OPTIONS: GetErrorReasonOptions = {
  context: BackendErrorContext.APPLICATION_LINK,
  overrides: {
    [BackendErrorText.APPLICATION_LIMIT_EXCEEDED]: msg`В команде уже максимум игроков. Обратитесь к организатору`,
  },
};

/**
 * Ответ сервера был: иначе это сеть, а не отказ бэкенда. Две формы: ошибка axios (`{ response: { status } }`,
 * отправка заявки) и ошибка RTK Query (`{ status, data }` из `restBaseQuery`, расшифровка токена) — у неё без
 * ответа нет числового статуса.
 */
function readResponse(error: unknown): { status?: number } | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { response, status } = error as { response?: { status?: number }; status?: unknown };
  if (response) return response;
  return typeof status === 'number' ? { status } : undefined;
}

function linkErrorText(error: unknown): BackendErrorText | undefined {
  return getBackendErrorText(error, { context: BackendErrorContext.APPLICATION_LINK });
}

/** Текст для отказа при отправке заявки (`POST /application/create-from-token-v2`). */
export function getApplicationErrorMessage(error: unknown): ApplicationErrorResult {
  const response = readResponse(error);

  if (!response) return { message: i18n._(NETWORK), applicationCreated: false };

  // nginx обрывает запрос до приложения и отвечает HTML — разбирать нечего.
  if (response.status === 413) {
    return {
      message: t`Фото слишком большое. Выберите файл меньше ${MAX_REQUEST_SIZE_MB} МБ`,
      applicationCreated: false,
    };
  }

  const reason = getErrorReason(error, LINK_REASON_OPTIONS);
  if (!reason) return { message: i18n._(GENERIC), applicationCreated: false };

  return {
    message: i18n._(reason),
    applicationCreated: linkErrorText(error) === BackendErrorText.LINK_PHOTO_UPLOAD_FAILED,
  };
}

/**
 * Ответа не было вовсе: сеть, а не отказ бэкенда.
 *
 * Отличается от прочих ошибок тем, что ссылка, скорее всего, цела — проблема на
 * стороне пользователя, и её имеет смысл предложить исправить.
 */
export function isNetworkError(error: unknown): boolean {
  return !readResponse(error);
}

/** Текст для отказа при расшифровке ссылки (`GET /application/decode-token`). */
export function getTokenErrorMessage(error: unknown): string {
  if (!readResponse(error)) return i18n._(NETWORK);

  // Турнир или команду удалили после выпуска ссылки — ссылка цела, данных нет: это тоже правила реестра.
  return i18n._(getErrorReason(error, LINK_REASON_OPTIONS) ?? BROKEN_LINK);
}

/**
 * Ссылка протухла: срок жизни токена равен дате окончания приёма заявок.
 *
 * Отдельная функция, потому что это единственный отказ, на который страница
 * отвечает не тостом, а собственным экраном.
 */
export function isExpiredTokenError(error: unknown): boolean {
  return linkErrorText(error) === BackendErrorText.LINK_TOKEN_EXPIRED;
}
